import http from './api'
import type { CityItem, CountryItem, PublicTournament, PublicParticipant, LeaderboardEntry, PublicPlayerPredictionItem, PublicProfile } from '../types/public'
import { asPublicEnrollmentList } from '../utils/publicFootballEnrollment'

export async function getCountries(): Promise<CountryItem[]> {
  const { data } = await http.get<CountryItem[]>('/countries')
  return data
}

export async function getCitiesByCountry(countryCode: string): Promise<CityItem[]> {
  const { data } = await http.get<CityItem[]>('/cities', { params: { countryCode } })
  return data
}

export async function getPublicTournaments(params?: { gameType?: string; active?: boolean }): Promise<PublicTournament[]> {
  const { data } = await http.get<PublicTournament[]>('/public-tournaments', { params })
  return data
}

export async function getActivePublicTournaments(gameType?: string): Promise<PublicTournament[]> {
  const params: Record<string, string> = { active: 'true' }
  if (gameType) params.gameType = gameType
  const { data } = await http.get<PublicTournament[]>('/public-tournaments', { params })
  return Array.isArray(data) ? data : []
}

export async function getPublicTournament(id: string): Promise<PublicTournament> {
  const { data } = await http.get<PublicTournament>(`/public-tournaments/${id}`)
  return data
}

export async function getPublicTournamentMatches(
  id: string,
  options?: { startDate?: string; endDate?: string }
): Promise<Array<{
  id: string
  date: string
  time: string
  teamA: string
  teamB: string
  league: string
  score: string | null
  winner: string | null
}>> {
  const params: Record<string, string> = {}
  if (options?.startDate) params.startDate = options.startDate
  if (options?.endDate) params.endDate = options.endDate
  const { data } = await http.get(`/public-tournaments/${id}/matches`, { params })
  return data
}

export async function enrollPublicParticipant(body: {
  publicTournamentId: string
  countryCode: string
  city: string
}): Promise<PublicParticipant> {
  const { data } = await http.post<PublicParticipant>('/public-participants', body)
  return data
}

export async function enrollPublicParticipantBulk(body: {
  tournamentIds: string[]
  countryCode: string
  city: string
}): Promise<PublicParticipant[]> {
  const { data } = await http.post<PublicParticipant[]>('/public-participants/bulk', body)
  return data
}

export interface UpcomingMatchItem {
  match: { id: string; date: string; time: string; teamA: string; teamB: string; league: string; score: string | null; winner: string | null; number?: string | null; liveScore?: string | null; liveStatus?: string | null }
  publicTournamentId: string
  publicParticipantId: string
  tournamentName: string
}

export async function getMyUpcomingMatches(options?: { startDate?: string; endDate?: string }): Promise<UpcomingMatchItem[]> {
  const params: Record<string, string> = {}
  if (options?.startDate) params.startDate = options.startDate
  if (options?.endDate) params.endDate = options.endDate
  const { data } = await http.get<UpcomingMatchItem[]>('/public-participants/me/upcoming-matches', { params })
  return data
}

export interface UpcomingRaceItem {
  race: import('../types/formula1').Formula1Race
  publicTournamentId: string
  publicParticipantId: string
  tournamentName: string
}

export async function getMyUpcomingRaces(): Promise<UpcomingRaceItem[]> {
  const { data } = await http.get<UpcomingRaceItem[]>('/public-participants/me/upcoming-races')
  return data
}

export interface PublicFormula1PredictionItem {
  id: string
  publicParticipantId: string
  raceId: string
  predictedPositions: Record<string, number>
  totalPoints: number
  createdAt: string
  updatedAt: string
  race?: import('../types/formula1').Formula1Race
}

export async function getMyPublicFormula1Predictions(participantId: string): Promise<PublicFormula1PredictionItem[]> {
  const { data } = await http.get<PublicFormula1PredictionItem[]>(`/public-participants/${participantId}/formula1-predictions`)
  return data
}

export async function submitPublicFormula1Prediction(
  publicTournamentId: string,
  body: { publicParticipantId: string; raceId: string; predictedPositions: Record<string, number> }
): Promise<PublicFormula1PredictionItem> {
  const { data } = await http.post<PublicFormula1PredictionItem>(
    `/public-tournaments/${publicTournamentId}/formula1-predictions`,
    body
  )
  return data
}

export const QUERY_KEY_MY_PUBLIC_ENROLLMENTS = ['my-public-enrollments'] as const
export const QUERY_KEY_PUBLIC_TOURNAMENTS = 'public-tournaments' as const

export async function getMyPublicEnrollments(): Promise<PublicParticipant[]> {
  const { data } = await http.get<PublicParticipant[]>('/public-participants/me', {
    params: { _: Date.now() },
  })
  return asPublicEnrollmentList(data)
}

export async function leaveWorldFootball(body?: { tournamentIds?: string[] }): Promise<{
  removedTournamentIds: string[]
  leftAllFootball: boolean
}> {
  const { data } = await http.post('/public-participants/me/leave-football', body ?? {})
  return data
}

export async function leaveWorldFormula1(): Promise<{ left: boolean }> {
  const { data } = await http.post('/public-participants/me/leave-formula1', {})
  return data
}

export async function updatePublicParticipantLocation(
  participantId: string,
  body: { countryCode: string; city: string }
): Promise<PublicParticipant> {
  const { data } = await http.patch<PublicParticipant>(`/public-participants/${participantId}/location`, body)
  return data
}

export async function updatePublicParticipantPreferences(
  participantId: string,
  body: { countryCode?: string; city?: string; favoriteTournamentIds?: string[] }
): Promise<PublicParticipant> {
  const { data } = await http.patch<PublicParticipant>(`/public-participants/${participantId}/preferences`, body)
  return data
}

export async function getMyParticipantForTournament(publicTournamentId: string): Promise<PublicParticipant | null> {
  try {
    const { data } = await http.get<PublicParticipant>(`/public-participants/tournament/${publicTournamentId}`)
    return data
  } catch (e: any) {
    if (e?.response?.status === 404) return null
    throw e
  }
}

export async function submitPublicPrediction(
  publicTournamentId: string,
  body: {
    publicParticipantId: string
    matchId: string
    winner?: string
    predictedScore: string
    predictedWinnerAdditionalTime?: string
    predictedWinnerPenalties?: string
  }
): Promise<unknown> {
  const { data } = await http.post(`/public-tournaments/${publicTournamentId}/predictions`, body)
  return data
}

export type LeaderboardScope = 'world' | 'continent' | 'country' | 'city'

export async function getPublicLeaderboard(
  publicTournamentId: string,
  scope: LeaderboardScope,
  filters?: { continentCode?: string; countryCode?: string; city?: string }
): Promise<LeaderboardEntry[]> {
  const params: Record<string, string> = { scope }
  if (filters?.continentCode) params.continentCode = filters.continentCode
  if (filters?.countryCode) params.countryCode = filters.countryCode
  if (filters?.city) params.city = filters.city
  const { data } = await http.get<LeaderboardEntry[]>(`/public-tournaments/${publicTournamentId}/leaderboard`, { params })
  return data
}

export async function getPublicLeaderboardCombined(
  publicTournamentIds: string[],
  scope: LeaderboardScope,
  filters?: { continentCode?: string; countryCode?: string; city?: string }
): Promise<LeaderboardEntry[]> {
  if (publicTournamentIds.length === 0) return []
  const params: Record<string, string> = {
    tournamentIds: publicTournamentIds.join(','),
    scope,
  }
  if (filters?.continentCode) params.continentCode = filters.continentCode
  if (filters?.countryCode) params.countryCode = filters.countryCode
  if (filters?.city) params.city = filters.city
  const { data } = await http.get<LeaderboardEntry[]>('/public-tournaments/leaderboard/combined', { params })
  return data
}

export async function getPublicPlayerPredictions(
  userId: string,
  publicTournamentIds: string[]
): Promise<PublicPlayerPredictionItem[]> {
  if (publicTournamentIds.length === 0) return []
  const { data } = await http.get<PublicPlayerPredictionItem[]>(
    `/public-tournaments/leaderboard/player/${userId}/predictions`,
    { params: { tournamentIds: publicTournamentIds.join(',') } }
  )
  return data
}

/** Public profile for any participant (name, location, join date, stats). */
export async function getPublicProfile(participantId: string): Promise<PublicProfile> {
  const { data } = await http.get<PublicProfile>(`/public-participants/${participantId}/public-profile`)
  return data
}

/** Public results (predictions with match result) for any participant. */
export async function getPublicResults(
  participantId: string,
  tournamentIds: string[]
): Promise<PublicPlayerPredictionItem[]> {
  const params = tournamentIds.length > 0 ? { tournamentIds: tournamentIds.join(',') } : {}
  const { data } = await http.get<PublicPlayerPredictionItem[]>(
    `/public-participants/${participantId}/public-results`,
    { params }
  )
  return data
}
